import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { languageServices, globalServices } from "../../Services";
import CardWrap from "../Common/CardWrap";
import ResultModal from "../Common/ResultModal";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../Contexts/AuthContext";
import { roles } from "../../Config/Roles";
import Tooltip from "../Common/Tooltip";
import { lettersOnly } from "../../Config/Regex";

const AddLanguageModal = ({ show, setShow }) => {
  const { t: translator } = useTranslation();
  const { roleId } = useContext(AuthContext);
  const { master, admin, support } = roles;
  const { register, handleSubmit, errors } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const [languages, setLanguages] = useState([]);
  const history = useHistory();
  const [modal, setModal] = useState({
    title: "",
    text: "",
    show: false,
    btnClass: "",
    success: true,
  });

  useEffect(() => {
    ![master, admin, support].includes(Number(roleId))
      ? history.push("/error-page/401")
      : globalServices.getLanguages().then((data) => setLanguages(data.data));
  }, []);

  const goBack = () => {
    history.push("/language");
  };

  const handleClose = () => {
    setModal({ show: false });
    if (modal.success === true) {
      goBack();
    }
  };

  const onSubmit = (values) => {
    languageServices
      .create({
        name: values.name,
        shortName: values.shortName,
        cultureCode: values.cultureCode,
        copyLanguageId: Number(values.copyTranslationFromLanguageId),
      })
      .then((res) => {
        res.data.success
          ? setModal({
              show: true,
              title: translator("AddLanguage-SuccessModal-Title"),
              text: translator("AddLanguage-SuccessModal-Text"),
              btnClass: "btn btn-primary",
              success: true,
            })
          : setModal({
              show: true,
              title: translator("AddLanguage-UnsuccessModal-Title"),
              text: translator("AddLanguage-UnsuccessModal-Text"),
              btnClass: "btn btn-primary",
              success: false,
            });
      })
      .catch((err) =>
        setModal({
          show: true,
          title: translator("AddLanguage-UnsuccessModal-Title"),
          text: translator("AddLanguage-UnsuccessModal-Text"),
          btnClass: "btn btn-primary",
          success: false,
        })
      );
  };

  const handleCancel = () => {
    history.push("/language");
  };

  return (
    <CardWrap subtitle={translator("AddLanguage-Title")} backUrl={"/language"}>
      <form onSubmit={handleSubmit(onSubmit)} className="row">
        <div className="col-lg-4">
          <fieldset className="scheduler-border">
            <div className="form-group">
              <label>{translator("AddLanguage-Name")}</label>{" "}
              <Tooltip text={translator("Language-Name-Msg")} />
              <input
                type="text"
                className="form-control"
                name="name"
                ref={register({
                  required: true,
                  pattern: lettersOnly,
                  maxLength: 20,
                })}
              />
              {errors.name && errors.name.type === "required" && (
                <p className="text-danger">
                  {translator("ValidationMsg-RequiredField")}
                </p>
              )}
              {errors.name && errors.name.type === "pattern" && (
                <p className="text-danger">
                  {translator("ValidationMsg-LettersOnly")}
                </p>
              )}
              {errors.name && errors.name.type === "maxLength" && (
                <p className="text-danger">
                  {translator("ValidationMsg-MaxLength20")}
                </p>
              )}
            </div>

            <div className="form-group">
              <label>{translator("AddLanguage-Code")}</label>{" "}
              <Tooltip text={translator("Language-Code-Msg")} />
              <input
                type="text"
                className="form-control"
                name="shortName"
                ref={register({
                  required: true,
                  pattern: lettersOnly,
                  maxLength: 3,
                })}
              />
              {errors.shortName && errors.shortName.type === "required" && (
                <p className="text-danger">
                  {translator("ValidationMsg-RequiredField")}
                </p>
              )}
              {errors.shortName && errors.shortName.type === "pattern" && (
                <p className="text-danger">
                  {translator("ValidationMsg-LettersOnly")}
                </p>
              )}
              {errors.shortName && errors.shortName.type === "maxLength" && (
                <p className="text-danger">
                  {translator("ValidationMsg-MaxLength-ThreeCharacters")}
                </p>
              )}
            </div>

            <div className="form-group">
              <label>{translator("AddLanguage-CultureCode")}</label>{" "}
              <Tooltip text={translator("Language-CultureCode-Msg")} />
              <input
                type="text"
                className="form-control"
                name="cultureCode"
                ref={register({ required: true, maxLength: 10 })}
              />
              {errors.cultureCode && errors.cultureCode.type === "required" && (
                <p className="text-danger">
                  {translator("ValidationMsg-RequiredField")}
                </p>
              )}
              {errors.cultureCode &&
                errors.cultureCode.type === "maxLength" && (
                  <p className="text-danger">
                    {translator("ValidationMsg-MaxLength10")}
                  </p>
                )}
            </div>

            <div className="form-group">
              <label htmlFor="ddlStore">
                {translator("AddLanguage-CopyTranslations")}
              </label>{" "}
              <Tooltip text={translator("Language-CopyTranslations-Msg")} />
              <select
                name="copyTranslationFromLanguageId"
                ref={register({
                  validate: (value) => value !== "-1",
                })}
                className="form-control"
              >
                <option value="-1">
                  {translator("AddLanguage-ChooseSelect")}
                </option>
                {languages &&
                  languages.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </select>
              {errors.copyTranslationFromLanguageId &&
                errors.copyTranslationFromLanguageId.type === "validate" && (
                  <p className="text-danger">
                    {translator("ValidationMsg-RequiredField")}
                  </p>
                )}
            </div>
          </fieldset>
        </div>

        <div
          className="container-fluid"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <button className="btn btn-outline-secondary" onClick={handleCancel}>
            {translator("Btn-Cancel")}
          </button>
          &nbsp;
          <button className="btn btn-primary" type="submit">
            {translator("Btn-Save")}
          </button>
        </div>
      </form>
      <ResultModal
        show={modal.show}
        title={modal.title}
        text={modal.text}
        btnClass={modal.btnClass}
        handleClose={handleClose}
      />
    </CardWrap>
  );
};

export default AddLanguageModal;
